// Google Calendar & ICS calendar utilities

/**
 * Clean and format time strings (like '9:00 AM - 10:00 AM') into clean 'HH:MM'
 */
const cleanTime = (timeStr) => {
  if (!timeStr) return '12:00';
  if (/^\d{2}:\d{2}$/.test(timeStr)) {
    return timeStr;
  }
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = match[3] ? match[3].toUpperCase() : null;
    
    if (ampm === 'PM' && hours < 12) {
      hours += 12;
    } else if (ampm === 'AM' && hours === 12) {
      hours = 0;
    }
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }
  return '12:00';
};

/**
 * Generate a Google Calendar event URL
 * Opens Google Calendar with a pre-filled event
 */
export const generateGoogleCalendarUrl = ({ title, date, time, duration = 60, location, description }) => {
  const baseUrl = 'https://calendar.google.com/calendar/render';
  
  // Build datetime strings in YYYYMMDDTHHMMSS format
  let startDateTime, endDateTime;
  
  if (time) {
    const cleanTimeStr = cleanTime(time);
    // Parse date and time
    const [year, month, day] = date.split('-');
    const [hours, minutes] = cleanTimeStr.split(':');
    startDateTime = `${year}${month}${day}T${hours}${minutes}00`;
    
    // Calculate end time
    const startDate = new Date(`${date}T${cleanTimeStr}`);
    startDate.setMinutes(startDate.getMinutes() + duration);
    const endHours = String(startDate.getHours()).padStart(2, '0');
    const endMins = String(startDate.getMinutes()).padStart(2, '0');
    const endDay = String(startDate.getDate()).padStart(2, '0');
    const endMonth = String(startDate.getMonth() + 1).padStart(2, '0');
    const endYear = startDate.getFullYear();
    endDateTime = `${endYear}${endMonth}${endDay}T${endHours}${endMins}00`;
  } else {
    // All-day event
    const [year, month, day] = date.split('-');
    startDateTime = `${year}${month}${day}`;
    
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    const ndYear = nextDay.getFullYear();
    const ndMonth = String(nextDay.getMonth() + 1).padStart(2, '0');
    const ndDay = String(nextDay.getDate()).padStart(2, '0');
    endDateTime = `${ndYear}${ndMonth}${ndDay}`;
  }

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${startDateTime}/${endDateTime}`,
  });

  if (location) params.set('location', location);
  if (description) params.set('details', description);

  return `${baseUrl}?${params.toString()}`;
};

/**
 * Generate ICS calendar file content
 */
export const generateICSContent = ({ title, date, time, duration = 60, location, description }) => {
  const now = new Date();
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}@placementpoint.ng`;
  
  const formatICSDate = (d) => {
    return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  let dtStart, dtEnd;

  if (time) {
    const cleanTimeStr = cleanTime(time);
    const start = new Date(`${date}T${cleanTimeStr}`);
    const end = new Date(start.getTime() + duration * 60000);
    dtStart = `DTSTART:${formatICSDate(start)}`;
    dtEnd = `DTEND:${formatICSDate(end)}`;
  } else {
    // All-day event
    const [year, month, day] = date.split('-');
    dtStart = `DTSTART;VALUE=DATE:${year}${month}${day}`;
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    const ndYear = nextDay.getFullYear();
    const ndMonth = String(nextDay.getMonth() + 1).padStart(2, '0');
    const ndDay = String(nextDay.getDate()).padStart(2, '0');
    dtEnd = `DTEND;VALUE=DATE:${ndYear}${ndMonth}${ndDay}`;
  }

  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Placement Point//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatICSDate(now)}`,
    dtStart,
    dtEnd,
    `SUMMARY:${escapeICS(title)}`,
    location ? `LOCATION:${escapeICS(location)}` : '',
    description ? `DESCRIPTION:${escapeICS(description)}` : '',
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT30M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  return icsLines.join('\r\n');
};

/**
 * Escape special characters for ICS format
 */
const escapeICS = (text) => {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
};

/**
 * Generate a data URI for downloading an ICS file
 */
export const generateICSDataUri = (icsContent) => {
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
};

/**
 * Trigger a download of an ICS file in the browser
 */
export const downloadICSFile = (icsContent, filename = 'event.ics') => {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Format a date + time for display
 */
export const formatEventDateTime = (date, time) => {
  if (!date) return '';
  const d = new Date(date);
  const dateStr = d.toLocaleDateString('en-NG', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  if (time) {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${dateStr} at ${h12}:${minutes} ${ampm}`;
  }
  return dateStr;
};

export const WAT_OFFSET = '+01:00';

/**
 * Check if a booking has expired based on preferred date, preferred time (slot), and status.
 * Uses a timezone-aware WAT (+01:00) comparison to be consistent.
 */
export const checkBookingExpired = (booking) => {
  if (!booking) return false;
  if (booking.status !== 'pending' && booking.status !== 'expired') return false;
  
  let timeStr = '23:59';
  if (booking.preferred_time) {
    const parts = booking.preferred_time.split('-');
    // Use the end of the slot if available, e.g. '9:00 AM - 10:00 AM' -> '10:00 AM'
    const timePart = (parts[1] || parts[0])?.trim();
    if (timePart) {
      timeStr = cleanTime(timePart);
    }
  }

  // Construct a canonical timezone-aware date object (WAT / West Africa Time +01:00)
  const expiryDate = new Date(`${booking.preferred_date}T${timeStr}:00${WAT_OFFSET}`);
  return expiryDate < new Date();
};
